




































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import discountDialog from '@/views/wheelOfFortune/components/discountDialog.vue';
import { createCouponRule, getCouponDetail, getCouponList } from '@/services/coupon';
import {
  buildCouponRuleCommandFromPopDiscount,
  buildUnifiedPopDiscountPayload,
  hasLegacyInlinePopDiscount,
  mapCouponDetailToPopDiscount,
  normalizeCouponName,
  COUPON_NAME_MAX_LENGTH,
  stripFrontendDiscountMeta,
} from '@/utils/couponRulePayload';

@Component({
  name: 'PopUnifiedDiscountSettingsDialog',
  components: { discountDialog },
})
export default class PopUnifiedDiscountSettingsDialog extends Vue {
  @Prop({ type: Boolean, default: false }) visible!: boolean;
  @Prop({ default: null }) discountData!: Record<string, any> | null;

  innerVisible = false;
  /** 保存关闭时不触发 cancel，避免父级 save 后再跑 close 把状态清乱 */
  private suppressCancel = false;
  convertVisible = false;
  ruleDialogVisible = false;
  linkType: 'create' | 'select' = 'create';

  couponId = '';
  selectedCouponId = '';
  reserveCode = '';
  reserveError = '';
  ruleSelectError = '';
  ruleOptions: Array<{ label: string; value: string }> = [];
  ruleLoading = false;
  unifiedSaving = false;
  unifiedMode = false;
  inlineDiscount: Record<string, any> | null = null;
  createRuleDiscountData: Record<string, any> | null = null;
  convertCreateDiscountData: Record<string, any> | null = null;

  get showConversionBox() {
    return this.innerVisible && !this.unifiedMode && !this.couponId && hasLegacyInlinePopDiscount(this.inlineDiscount);
  }

  get showRuleSection() {
    return this.unifiedMode || Boolean(this.couponId) || !hasLegacyInlinePopDiscount(this.inlineDiscount);
  }

  get rulePlaceholder() {
    if (this.couponId) return '';
    return this.ruleOptions.length ? '请选择折扣码规则' : '当前无可用折扣码规则';
  }

  get ruleSelectDisabled() {
    return !this.ruleLoading && !this.ruleOptions.length && !this.couponId;
  }

  get convertRulePlaceholder() {
    if (this.selectedCouponId) return '';
    return this.ruleOptions.length ? '请选择折扣码规则' : '当前无可用折扣码规则';
  }

  @Watch('visible', { immediate: true })
  async onVisibleChange(val: boolean) {
    this.innerVisible = val;
    if (val) {
      await this.resetFromInitial();
    }
  }

  @Watch('innerVisible')
  onInnerVisibleChange(val: boolean) {
    this.$emit('update:visible', val);
    if (!val && !this.suppressCancel) {
      this.$emit('cancel');
    }
  }

  @Watch('linkType')
  onLinkTypeChange(val: 'create' | 'select') {
    if (val === 'select' && this.convertVisible) {
      this.fetchRuleOptions();
    }
  }

  @Watch('convertVisible')
  onConvertVisibleChange(val: boolean) {
    if (val && this.linkType === 'select') {
      this.fetchRuleOptions();
    }
  }

  /** 保证当前选中规则在 options 中有 label，避免 el-select 回显 couponId */
  ensureRuleOption(couponId: string, couponName?: string) {
    if (!couponId) return;
    const label = (couponName || '').trim();
    const idx = this.ruleOptions.findIndex((item) => item.value === couponId);
    if (idx >= 0) {
      if (label && this.ruleOptions[idx].label !== label) {
        this.$set(this.ruleOptions, idx, { value: couponId, label });
      }
      return;
    }
    this.ruleOptions = [{ value: couponId, label: label || couponId }, ...this.ruleOptions];
  }

  async resetFromInitial() {
    const data = this.discountData ? JSON.parse(JSON.stringify(this.discountData)) : null;
    this.inlineDiscount = data;
    this.reserveCode = data?.reserveCode || '';
    this.reserveError = '';
    this.ruleSelectError = '';

    const nextCouponId = data?.couponId || '';
    const nextCouponName = (data?.couponName || '').trim();
    this.unifiedMode = Boolean(nextCouponId || !hasLegacyInlinePopDiscount(data));
    this.linkType = nextCouponId ? 'select' : 'create';

    const needRuleList =
      this.unifiedMode || Boolean(nextCouponId) || !hasLegacyInlinePopDiscount(data);

    if (nextCouponId && nextCouponName) {
      // 本地已有名称：先写入 options 再赋 v-model，列表后台刷新
      this.ensureRuleOption(nextCouponId, nextCouponName);
      this.couponId = nextCouponId;
      this.selectedCouponId = nextCouponId;
      if (needRuleList) {
        this.fetchRuleOptions();
      }
      return;
    }

    if (nextCouponId) {
      // 仅有 couponId：先拉列表再赋值，避免下拉短暂展示 ID
      if (needRuleList) {
        await this.fetchRuleOptions();
      }
      this.ensureRuleOption(nextCouponId, nextCouponName);
      this.couponId = nextCouponId;
      this.selectedCouponId = nextCouponId;
      return;
    }

    this.couponId = '';
    this.selectedCouponId = '';
    if (needRuleList) {
      this.fetchRuleOptions();
    }
  }

  async fetchRuleOptions() {
    this.ruleLoading = true;
    try {
      const { data: resp } = await getCouponList({ page: 1, pageSize: 100, channel: 'POPUP' });
      if (resp?.code === 0) {
        const list = (resp.data?.list || []).map((item: any) => ({
          label: item.couponName,
          value: item.couponId,
        }));
        const keepId =
          this.couponId ||
          this.selectedCouponId ||
          this.inlineDiscount?.couponId ||
          this.discountData?.couponId ||
          '';
        const keepName =
          this.inlineDiscount?.couponName ||
          this.discountData?.couponName ||
          this.ruleOptions.find((item) => item.value === keepId)?.label ||
          '';
        if (keepId && !list.some((item) => item.value === keepId)) {
          list.unshift({ label: keepName || keepId, value: keepId });
        }
        this.ruleOptions = list;
      }
    } catch {
      // 拦截器已提示
    } finally {
      this.ruleLoading = false;
    }
  }

  onRuleDropdownVisible(open: boolean) {
    if (open) {
      this.fetchRuleOptions();
    }
  }

  openCreateRuleModal() {
    this.createRuleDiscountData = null;
    this.ruleDialogVisible = true;
  }

  openConvertModal() {
    this.linkType = 'create';
    this.convertCreateDiscountData = this.buildConvertCreateDiscountData();
    this.convertVisible = true;
  }

  buildConvertCreateDiscountData() {
    if (!hasLegacyInlinePopDiscount(this.inlineDiscount)) {
      return null;
    }
    const legacy = JSON.parse(JSON.stringify(this.inlineDiscount));
    delete legacy.couponId;
    delete legacy.unifiedMode;
    delete legacy.linkType;
    return legacy;
  }

  async onSelectRule(nextCouponId: string) {
    if (!nextCouponId) {
      this.couponId = '';
      this.ruleSelectError = '';
      return;
    }
    const selectedLabel = this.ruleOptions.find((item) => item.value === nextCouponId)?.label;
    this.ensureRuleOption(nextCouponId, selectedLabel);
    this.couponId = nextCouponId;
    this.ruleSelectError = '';
    try {
      const { data: resp } = await getCouponDetail({ couponId: nextCouponId });
      if (resp?.code === 0 && resp.data) {
        this.inlineDiscount = mapCouponDetailToPopDiscount(resp.data, this.reserveCode);
        this.ensureRuleOption(nextCouponId, resp.data.couponName || selectedLabel);
        this.unifiedMode = true;
        this.linkType = 'select';
      }
    } catch {
      // 拦截器已提示
    }
  }

  async onRuleCreateSave(discount: Record<string, any>) {
    const rawName = (discount.couponName || '').trim();
    const couponName = normalizeCouponName(rawName);
    if (!couponName) {
      this.$message.error('请输入折扣码名称');
      return;
    }
    if (rawName.length > COUPON_NAME_MAX_LENGTH) {
      this.$message.error(`折扣码名称最多${COUPON_NAME_MAX_LENGTH}个字符`);
      return;
    }
    this.unifiedSaving = true;
    try {
      const command = buildCouponRuleCommandFromPopDiscount(couponName, discount);
      const { data: resp } = await createCouponRule(command);
      if (resp?.code !== 0 || !resp.data?.couponId) {
        this.$message.error(resp?.message || '创建折扣规则失败');
        return;
      }
      const nextCouponId = resp.data.couponId;
      const detailResp = await getCouponDetail({ couponId: nextCouponId });
      if (detailResp.data?.code !== 0 || !detailResp.data?.data) {
        this.$message.error(detailResp.data?.message || '加载折扣规则失败');
        return;
      }
      const detail = detailResp.data.data;
      this.inlineDiscount = mapCouponDetailToPopDiscount(detail, this.reserveCode);
      this.ensureRuleOption(nextCouponId, detail.couponName || couponName);
      this.couponId = nextCouponId;
      this.selectedCouponId = nextCouponId;
      this.unifiedMode = true;
      this.linkType = 'create';
      this.ruleDialogVisible = false;
      this.convertVisible = false;
      this.fetchRuleOptions();
      this.$message.success('折扣规则创建成功');
    } catch {
      // commonApi 拦截器已对 code!==0 弹过错误，此处勿再提示
    } finally {
      this.unifiedSaving = false;
    }
  }

  async onConvertConfirm() {
    if (this.linkType === 'select') {
      if (!this.selectedCouponId) {
        this.$message.error('请选择折扣码规则');
        return;
      }
      this.unifiedSaving = true;
      try {
        await this.onSelectRule(this.selectedCouponId);
        this.convertVisible = false;
      } finally {
        this.unifiedSaving = false;
      }
      return;
    }

    const convertForm = this.$refs.convertCreateForm as any;
    convertForm?.validateAndSave?.();
  }

  closeMainAfterSave() {
    this.suppressCancel = true;
    this.innerVisible = false;
    this.$nextTick(() => {
      this.suppressCancel = false;
    });
  }

  onMainSave() {
    if (!this.reserveCode.trim()) {
      this.reserveError = '备用折扣码不可为空';
      return;
    }
    this.reserveError = '';

    if (this.unifiedMode || this.couponId) {
      if (!this.couponId) {
        this.ruleSelectError = '请选择或创建折扣码规则';
        return;
      }
      const selectedName =
        this.ruleOptions.find((item) => item.value === this.couponId)?.label ||
        this.inlineDiscount?.couponName ||
        '';
      this.$emit('save', buildUnifiedPopDiscountPayload(
        { ...(this.inlineDiscount || {}), couponName: selectedName },
        this.couponId,
        this.reserveCode
      ));
      this.closeMainAfterSave();
      return;
    }

    if (!this.inlineDiscount) {
      this.$message.error('请创建折扣码规则');
      return;
    }

    this.$emit('save', stripFrontendDiscountMeta({
      ...this.inlineDiscount,
      reserveCode: this.reserveCode.trim(),
    }));
    this.closeMainAfterSave();
  }

  onMainCancel() {
    this.innerVisible = false;
  }
}
